import React, { useEffect, useMemo, useRef, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { withNamespaces } from 'react-i18next'
import { withRouter } from 'react-router-dom'
import LogoSvg from 'images/logos/logo-sm.svg'
import { useNavigation } from './context'
import { buildAreas, buildSections, areasToStore, flatten, search, isActivePath } from './model'
import { useRemoteSearch, mergeResults, GROUP_LABELS } from './remoteSearch'
import * as S from './styles'

export { NavigationProvider, useNavigation } from './context'

const HOME = { key: 'home', to: '/', icon: 'fas fa-home' }

const readSavedOrder = (tenant) => {
  try {
    return JSON.parse(window.localStorage.getItem(`${tenant}-menuOptions`) || '[]') || []
  } catch (e) {
    return []
  }
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '')

/* ------------------------------------------------------------------ */
/* Peças da lista                                                      */
/* ------------------------------------------------------------------ */

const Badges = ({ badges, t }) =>
  (badges || []).map((kind) => (
    <S.Badge key={kind} kind={kind}>
      {kind === 'updated'
        ? t('nav.badgeUpdated', 'Repaginado')
        : kind === 'beta'
        ? t('nav.badgeBeta', 'Beta')
        : t('nav.badgeNew', 'Novo')}
    </S.Badge>
  ))

const Sort = ({ onUp, onDown, first, last, t }) => (
  <S.SortButtons
    onClick={(event) => {
      event.preventDefault()
      event.stopPropagation()
    }}
  >
    <button
      type="button"
      disabled={first}
      onClick={onUp}
      aria-label={t('nav.moveUp', 'Mover para cima')}
    >
      <i className="fas fa-chevron-up" />
    </button>
    <button
      type="button"
      disabled={last}
      onClick={onDown}
      aria-label={t('nav.moveDown', 'Mover para baixo')}
    >
      <i className="fas fa-chevron-down" />
    </button>
  </S.SortButtons>
)

/** Conteúdo de uma seção: páginas e, em Estações/Planejamento, as áreas com as estações. */
const SectionBody = ({ section, ui }) => {
  const { t, pathname, touch, editing, go, openAreas, toggleArea, moveArea, moveStation } = ui
  const exact = !!section.areas
  return (
    <>
      {section.items.map((item) => (
        <S.SubLink
          key={item.key}
          to={item.to}
          touch={touch ? 1 : 0}
          active={isActivePath(pathname, item.to, exact) ? 1 : 0}
          title={item.hint}
          onClick={go}
        >
          <i className={`nav-icon ${item.icon || 'far fa-circle'}`} aria-hidden="true" />
          <span className="label">{item.label}</span>
          <Badges badges={item.badges} t={t} />
        </S.SubLink>
      ))}
      {(section.areas || []).map((area, index) => {
        const areaKey = `${section.key}:${area.id}`
        const containsActive =
          isActivePath(pathname, area.to, true) ||
          area.stations.some((station) => isActivePath(pathname, station.to, true))
        const open =
          editing || (openAreas[areaKey] !== undefined ? openAreas[areaKey] : containsActive)
        const compact = area.stations.every((station) => String(station.label).length <= 9)
        const Stations = compact && !editing ? S.StationGrid : React.Fragment
        return (
          <div key={area.id}>
            <S.AreaRow>
              <S.SubButton
                as={editing ? 'div' : undefined}
                type={editing ? undefined : 'button'}
                touch={touch ? 1 : 0}
                open={open ? 1 : 0}
                current={containsActive ? 1 : 0}
                onClick={() => toggleArea(areaKey, !open)}
                aria-expanded={!!open}
              >
                <i className="chevron fas fa-chevron-right" aria-hidden="true" />
                <span className="label">{area.label}</span>
                {editing ? (
                  <Sort
                    t={t}
                    first={index === 0}
                    last={index === section.areas.length - 1}
                    onUp={() => moveArea(index, -1)}
                    onDown={() => moveArea(index, 1)}
                  />
                ) : (
                  <span className="count">{area.stations.length}</span>
                )}
              </S.SubButton>
              {!editing && (
                <S.SubLink
                  to={area.to}
                  touch={touch ? 1 : 0}
                  active={isActivePath(pathname, area.to, true) ? 1 : 0}
                  data-active={isActivePath(pathname, area.to, true) ? 1 : 0}
                  onClick={go}
                  title={t('nav.areaOverviewOf', {
                    defaultValue: 'Visão geral de {{area}}',
                    area: area.label,
                  })}
                  style={{ flex: 'none', width: touch ? 44 : 34, justifyContent: 'center' }}
                >
                  <i className="nav-icon fas fa-th-large" aria-hidden="true" />
                </S.SubLink>
              )}
            </S.AreaRow>
            {open && (
              <S.SubList>
                <Stations {...(compact && !editing ? { touch: touch ? 1 : 0 } : {})}>
                  {area.stations.map((station, stationIndex) => (
                    <S.SubLink
                      key={station.id}
                      as={editing ? 'div' : undefined}
                      to={station.to}
                      touch={touch ? 1 : 0}
                      active={isActivePath(pathname, station.to, true) ? 1 : 0}
                      onClick={go}
                      title={station.label}
                    >
                      <span className="label">{station.label}</span>
                      {editing && (
                        <Sort
                          t={t}
                          first={stationIndex === 0}
                          last={stationIndex === area.stations.length - 1}
                          onUp={() => moveStation(area.id, stationIndex, -1)}
                          onDown={() => moveStation(area.id, stationIndex, 1)}
                        />
                      )}
                    </S.SubLink>
                  ))}
                </Stations>
              </S.SubList>
            )}
          </div>
        )
      })}
    </>
  )
}

/** Início + seções em sanfona (computador aberto e gaveta). */
const MenuList = ({ sections, ui }) => {
  const { t, pathname, touch, editing, go, openSections, toggleSection, moveSection } = ui
  return (
    <>
      <S.RowLink
        to="/"
        touch={touch ? 1 : 0}
        active={isActivePath(pathname, '/', true) ? 1 : 0}
        onClick={go}
      >
        <i className={`nav-icon ${HOME.icon}`} aria-hidden="true" />
        <span className="label">{t('pageTitle.home', 'Início')}</span>
      </S.RowLink>
      {sections.map((section, index) => {
        const containsActive = sectionIsActive(section, pathname)
        const sort = editing && (
          <Sort
            t={t}
            first={index === 0}
            last={index === sections.length - 1}
            onUp={() => moveSection(index, -1)}
            onDown={() => moveSection(index, 1)}
          />
        )
        if (section.to)
          return (
            <S.Group key={section.key}>
              <S.RowLink
                as={editing ? 'div' : undefined}
                to={section.to}
                touch={touch ? 1 : 0}
                active={containsActive ? 1 : 0}
                onClick={go}
              >
                <i className={`nav-icon ${section.icon}`} aria-hidden="true" />
                <span className="label">{section.label}</span>
                {sort}
              </S.RowLink>
            </S.Group>
          )
        const open =
          openSections[section.key] !== undefined ? openSections[section.key] : containsActive
        return (
          <S.Group key={section.key}>
            <S.RowButton
              as={editing ? 'div' : undefined}
              type={editing ? undefined : 'button'}
              touch={touch ? 1 : 0}
              open={open ? 1 : 0}
              current={containsActive ? 1 : 0}
              aria-expanded={!!open}
              onClick={() => toggleSection(section.key, !open)}
            >
              <i className={`nav-icon ${section.icon}`} aria-hidden="true" />
              <span className="label">{section.label}</span>
              {sort || <i className="chevron fas fa-chevron-right" aria-hidden="true" />}
            </S.RowButton>
            {open && (
              <S.SubList>
                <SectionBody section={section} ui={ui} />
              </S.SubList>
            )}
          </S.Group>
        )
      })}
    </>
  )
}

const sectionIsActive = (section, pathname) =>
  (section.to && isActivePath(pathname, section.to)) ||
  section.items.some((item) => isActivePath(pathname, item.to, !!section.areas)) ||
  (section.areas || []).some(
    (area) =>
      isActivePath(pathname, area.to, true) ||
      area.stations.some((station) => isActivePath(pathname, station.to, true))
  )

/**
 * Resultado da busca: telas e estações do menu e, do servidor, ordens, itens e lotes, com um título
 * por grupo. As setas do teclado andam pela lista inteira.
 */
const Results = ({ results, ui, cursor, onHover, remote }) => {
  const { t, pathname, touch, go } = ui
  const searching = remote && remote.loading
  if (results.length === 0)
    return searching ? (
      <S.Empty>
        <i className="fas fa-circle-notch fa-spin" aria-hidden="true" />{' '}
        {t('nav.searchingRemote', 'Buscando ordens, itens e lotes...')}
      </S.Empty>
    ) : (
      <S.Empty>
        <b>{t('nav.noResults', 'Nada encontrado')}</b>
        {remote && remote.enabled
          ? t(
              'nav.noResultsHintRemote',
              'Tente o código da OP, do item ou do lote, ou o nome da tela.'
            )
          : t('nav.noResultsHint', 'Tente o nome da estação, da área ou da tela.')}
      </S.Empty>
    )
  const showHeaders = results.some((entry) => entry.group && entry.group !== 'menu')
  return (
    <>
      {results.map((entry, index) => {
        const header =
          showHeaders && (index === 0 || results[index - 1].group !== entry.group) ? (
            <S.ResultGroup key={`group-${entry.group}`}>
              {t(`nav.searchGroup.${entry.group}`, GROUP_LABELS[entry.group] || '')}
            </S.ResultGroup>
          ) : null
        return (
          <React.Fragment key={entry.key}>
            {header}
            <S.RowLink
              to={entry.to}
              touch={touch ? 1 : 0}
              active={index === cursor || isActivePath(pathname, entry.to, true) ? 1 : 0}
              onClick={go}
              onMouseEnter={onHover ? () => onHover(index) : undefined}
              style={{ minHeight: touch ? 54 : 46 }}
            >
              <i className={`nav-icon ${entry.icon || 'far fa-circle'}`} aria-hidden="true" />
              <span className="label">
                {entry.label}
                {entry.path && <S.ResultPath>{entry.path}</S.ResultPath>}
              </span>
            </S.RowLink>
          </React.Fragment>
        )
      })}
      {searching && (
        <S.ResultGroup>
          <i className="fas fa-circle-notch fa-spin" aria-hidden="true" />{' '}
          {t('nav.searchingRemote', 'Buscando ordens, itens e lotes...')}
        </S.ResultGroup>
      )}
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Painel aberto (barra lateral larga e gaveta)                        */
/* ------------------------------------------------------------------ */

const Panel = ({ sections, entries, ui, foot, autoFocus, showSearch }) => {
  const { t, touch, history, go, focusSearch } = ui
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const input = useRef(null)
  const menuResults = useMemo(() => search(entries, query), [entries, query])
  const remote = useRemoteSearch(showSearch ? query : '', t)
  const results = useMemo(() => mergeResults(menuResults, remote), [menuResults, remote])

  useEffect(() => setCursor(0), [query])
  useEffect(() => {
    if ((focusSearch || autoFocus) && input.current) input.current.focus()
  }, [focusSearch, autoFocus])

  const onKeyDown = (event) => {
    if (!query) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor((value) => Math.min(value + 1, results.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor((value) => Math.max(value - 1, 0))
    } else if (event.key === 'Enter' && results[cursor]) {
      history.push(results[cursor].to)
      setQuery('')
      go()
    } else if (event.key === 'Escape') {
      setQuery('')
    }
  }

  return (
    <>
      {showSearch && (
        <S.Search touch={touch ? 1 : 0}>
          <i className="fas fa-search" aria-hidden="true" />
          <input
            ref={input}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              remote.enabled
                ? t('nav.searchPlaceholderRemote', 'Buscar tela, estação, OP, item ou lote')
                : t('nav.searchPlaceholder', 'Buscar estação ou tela')
            }
            aria-label={t('nav.search', 'Buscar no menu')}
          />
          {!touch && !query && <kbd>{isMac ? '⌘K' : 'Ctrl K'}</kbd>}
          {query && (
            <S.IconButton
              type="button"
              size={26}
              onClick={() => setQuery('')}
              aria-label={t('nav.clearSearch', 'Limpar busca')}
            >
              <i className="fas fa-times" />
            </S.IconButton>
          )}
        </S.Search>
      )}
      <S.Scroll>
        {query ? (
          <Results
            results={results}
            cursor={cursor}
            remote={remote}
            ui={{
              ...ui,
              go: () => {
                setQuery('')
                go()
              },
            }}
          />
        ) : (
          <MenuList sections={sections} ui={ui} />
        )}
      </S.Scroll>
      {foot}
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Trilho de ícones (barra lateral recolhida)                          */
/* ------------------------------------------------------------------ */

const RailView = ({ sections, ui }) => {
  const { t, pathname } = ui
  const [flyout, setFlyout] = useState(null)
  const [flyTop, setFlyTop] = useState(0)
  const [tip, setTip] = useState(null)
  const ref = useRef(null)

  useEffect(() => setFlyout(null), [pathname])
  useEffect(() => {
    if (!flyout) return undefined
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setFlyout(null)
    }
    const onKey = (event) => event.key === 'Escape' && setFlyout(null)
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [flyout])

  const section = sections.find((item) => item.key === flyout)

  // dica com o nome ao passar o mouse (o trilho corta o que sai dele, então a dica é fixa)
  const hint = (label) => ({
    onMouseEnter: (event) => {
      const box = event.currentTarget.getBoundingClientRect()
      setTip({ label, top: box.top + box.height / 2 })
    },
    onMouseLeave: () => setTip(null),
    onFocus: (event) => {
      const box = event.currentTarget.getBoundingClientRect()
      setTip({ label, top: box.top + box.height / 2 })
    },
    onBlur: () => setTip(null),
    'aria-label': label,
  })

  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <S.Rail>
        <S.RailLink
          to="/"
          active={isActivePath(pathname, '/', true) ? 1 : 0}
          {...hint(t('pageTitle.home', 'Início'))}
        >
          <i className={HOME.icon} aria-hidden="true" />
        </S.RailLink>
        {sections.map((item) => {
          const active = sectionIsActive(item, pathname)
          if (item.to)
            return (
              <S.RailLink key={item.key} to={item.to} active={active ? 1 : 0} {...hint(item.label)}>
                <i className={item.icon} aria-hidden="true" />
              </S.RailLink>
            )
          return (
            <S.RailButton
              key={item.key}
              type="button"
              active={active ? 1 : 0}
              open={flyout === item.key ? 1 : 0}
              aria-expanded={flyout === item.key}
              {...hint(item.label)}
              onClick={(event) => {
                const box = event.currentTarget.getBoundingClientRect()
                const room = window.innerHeight - 16
                setTip(null)
                setFlyTop(Math.max(8, Math.min(box.top - 8, room - 420)))
                setFlyout(flyout === item.key ? null : item.key)
              }}
            >
              <i className={item.icon} aria-hidden="true" />
            </S.RailButton>
          )
        })}
      </S.Rail>
      {tip && !flyout && <S.RailTip style={{ top: tip.top }}>{tip.label}</S.RailTip>}
      {section && (
        <S.Flyout style={{ top: flyTop }} role="menu">
          <div className="title">
            <i className={section.icon} aria-hidden="true" />
            {section.label}
          </div>
          <S.Scroll>
            <SectionBody section={section} ui={{ ...ui, go: () => setFlyout(null) }} />
          </S.Scroll>
        </S.Flyout>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Navegação do app                                                    */
/* ------------------------------------------------------------------ */

/** Caminho da tela atual para a barra do topo, ex.: Estações › Estoque › ST. ESTOQUE 1. */
const trailFor = (entries, pathname, t) => {
  if (isActivePath(pathname, '/', true)) return [t('pageTitle.home', 'Início')]
  const exact = entries.find((entry) => entry.to && isActivePath(pathname, entry.to, true))
  const match =
    exact ||
    entries
      .filter((entry) => entry.to && entry.to !== '/' && isActivePath(pathname, entry.to))
      .sort((a, b) => b.to.length - a.to.length)[0]
  if (!match) return []
  const section = (match.path || '').split(' · ')[0]
  if (String(match.key).includes('/area/')) return [section, match.label].filter(Boolean)
  return [...(match.path || '').split(' · '), match.label].filter(Boolean)
}

/** Busca da barra do topo (Ctrl+K / ⌘K): estações, áreas e telas do menu. */
export const TopSearch = withNamespaces()(
  withRouter(({ t, history, location }) => {
    const nav = useNavigation()
    const [query, setQuery] = useState('')
    const [open, setOpen] = useState(false)
    const [cursor, setCursor] = useState(0)
    const input = useRef(null)
    const box = useRef(null)
    const menuResults = useMemo(() => search(nav.entries || [], query), [nav.entries, query])
    const remote = useRemoteSearch(query, t)
    const results = useMemo(() => mergeResults(menuResults, remote), [menuResults, remote])

    useEffect(() => setCursor(0), [query])
    useEffect(() => {
      if (nav.focusSearch && nav.mode !== 'mobile' && input.current) {
        input.current.focus()
        setOpen(true)
      }
    }, [nav.focusSearch, nav.mode])
    useEffect(() => {
      if (!open) return undefined
      const close = (event) => box.current && !box.current.contains(event.target) && setOpen(false)
      document.addEventListener('mousedown', close)
      return () => document.removeEventListener('mousedown', close)
    }, [open])

    const done = () => {
      setQuery('')
      setOpen(false)
      if (input.current) input.current.blur()
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        done()
      } else if (!query) {
        return
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        setCursor((value) => Math.min(value + 1, results.length - 1))
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setCursor((value) => Math.max(value - 1, 0))
      } else if (event.key === 'Enter' && results[cursor]) {
        history.push(results[cursor].to)
        done()
      }
    }

    return (
      <S.TopSearch ref={box}>
        <label>
          <i className="fas fa-search" aria-hidden="true" />
          <input
            ref={input}
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              setQuery(event.target.value)
              setOpen(true)
            }}
            onKeyDown={onKeyDown}
            placeholder={
              remote.enabled
                ? t('nav.searchEverythingRemote', 'Buscar tela, estação, OP, item ou lote...')
                : t('nav.searchEverything', 'Buscar estação, área ou tela...')
            }
            aria-label={t('nav.search', 'Buscar no menu')}
          />
          {!query && <kbd>{isMac ? '⌘K' : 'Ctrl K'}</kbd>}
        </label>
        {open && query && (
          <S.TopResults role="listbox">
            {results.length > 0 && (
              <div className="hint">
                {t('nav.searchResults', {
                  defaultValue: '{{count}} resultado(s)',
                  count: results.length,
                })}
              </div>
            )}
            <Results
              results={results}
              cursor={cursor}
              onHover={setCursor}
              remote={remote}
              ui={{ t, pathname: location.pathname, touch: false, go: done }}
            />
          </S.TopResults>
        )}
      </S.TopSearch>
    )
  })
)

/**
 * Menu principal do portal. No computador vira barra lateral (larga ou só ícones), no tablet
 * vira gaveta e no celular ganha a barra de atalhos embaixo. A busca (Ctrl+K) fica na barra
 * colorida do topo; na gaveta ela também aparece no topo da lista. A ordem personalizada
 * ("Organizar menu") continua salva por ambiente, como no menu antigo.
 */
const AppNavigation = observer(({ rootStore, t, location, history }) => {
  const nav = useNavigation()
  const { mode, collapsed, drawerOpen } = nav
  const tenant = rootStore.authStore.selectedTenant
  const pathname = location.pathname
  const isStaging =
    window.location.hostname.includes('staging') || window.location.hostname.includes('localhost')

  const [savedOrder, setSavedOrder] = useState(() => readSavedOrder(tenant))
  const [editing, setEditing] = useState(false)
  const [openSections, setOpenSections] = useState({})
  const [openAreas, setOpenAreas] = useState({})

  useEffect(() => setSavedOrder(readSavedOrder(tenant)), [tenant])

  const roles = (rootStore.authStore.roles || []).slice()
  const customPages = (
    (rootStore.customPageStore && rootStore.customPageStore.customPages) ||
    []
  ).slice()
  const areas = buildAreas(
    (rootStore.areaStore.areas || []).slice(),
    (rootStore.authStore.filteredWorkStations || []).slice(),
    (rootStore.areaStore.areasMenu || []).slice()
  )
  const sections = buildSections({ t, roles, customPages, areas, savedOrder })
  const entries = flatten(sections, t)

  // a barra do topo mostra o caminho da tela atual e usa os mesmos destinos na busca
  const trail = trailFor(entries, pathname, t)
  const trailKey = trail.join(' › ')
  const entriesKey = entries.map((entry) => `${entry.key}=${entry.label}`).join('|')
  useEffect(() => {
    nav.setTrail(trail)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trailKey])
  useEffect(() => {
    nav.setEntries(entries)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entriesKey])

  // ao mudar de tela, a seção e a área da tela atual abrem sozinhas
  useEffect(() => {
    setOpenSections((prev) => {
      const next = { ...prev }
      sections.forEach((section) => {
        if (sectionIsActive(section, pathname)) next[section.key] = true
      })
      return next
    })
    if (mode !== 'sidebar') nav.closeDrawer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    // outros painéis fixos à esquerda (ex.: lista de eventos do Planejamento) começam depois do menu
    document.documentElement.style.setProperty(
      '--ps-nav-offset',
      mode !== 'sidebar' ? '0px' : `${collapsed ? S.RAIL_WIDTH : S.EXPANDED_WIDTH}px`
    )
  }, [mode, collapsed])

  // Esc fecha a gaveta
  useEffect(() => {
    if (!drawerOpen) return undefined
    const onKey = (event) => event.key === 'Escape' && nav.closeDrawer()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [drawerOpen, nav])

  const saveOrder = (list) => {
    const next = list.map((section, index) => ({
      key: section.key,
      path: section.key,
      order: index,
    }))
    setSavedOrder(next)
    try {
      window.localStorage.setItem(`${tenant}-menuOptions`, JSON.stringify(next))
    } catch (e) {
      // ignora
    }
  }

  const swap = (list, index, delta) => {
    const next = list.slice()
    const target = index + delta
    if (target < 0 || target >= next.length) return list
    ;[next[index], next[target]] = [next[target], next[index]]
    return next
  }

  const ui = {
    t,
    history,
    pathname,
    editing,
    touch: mode !== 'sidebar',
    focusSearch: mode === 'mobile' ? nav.focusSearch : 0,
    go: () => {
      if (mode !== 'sidebar') nav.closeDrawer()
    },
    openSections,
    toggleSection: (key, value) => setOpenSections((prev) => ({ ...prev, [key]: value })),
    openAreas,
    toggleArea: (key, value) => setOpenAreas((prev) => ({ ...prev, [key]: value })),
    moveSection: (index, delta) => saveOrder(swap(sections, index, delta)),
    moveArea: (index, delta) =>
      rootStore.areaStore.updateAreaOrdering(areasToStore(swap(areas, index, delta))),
    moveStation: (areaId, index, delta) =>
      rootStore.areaStore.updateAreaOrdering(
        areasToStore(
          areas.map((area) =>
            area.id === areaId ? { ...area, stations: swap(area.stations, index, delta) } : area
          )
        )
      ),
  }

  const foot = (
    <S.Foot collapsed={collapsed ? 1 : 0} hidden={mode === 'mobile'}>
      <button
        type="button"
        data-on={editing}
        onClick={() => setEditing(!editing)}
        title={t('nav.organizeHint', 'Mude a ordem das seções, áreas e estações')}
      >
        <i className={editing ? 'fas fa-check' : 'fas fa-sort-amount-down'} aria-hidden="true" />
        {editing ? t('nav.organizeDone', 'Concluir') : t('nav.organize', 'Organizar menu')}
      </button>
      {mode === 'sidebar' && (
        <button type="button" onClick={() => nav.setCollapsed(true)}>
          <i className="fas fa-angle-double-left" aria-hidden="true" />
          {t('nav.collapse', 'Recolher menu')}
        </button>
      )}
    </S.Foot>
  )

  if (mode === 'sidebar')
    return (
      <S.Sidebar collapsed={collapsed ? 1 : 0} aria-label={t('nav.main', 'Menu principal')}>
        <S.Brand to="/" collapsed={collapsed ? 1 : 0} staging={isStaging ? 1 : 0}>
          <span className="mark">
            <img src={LogoSvg} alt="" />
          </span>
          {!collapsed && <span className="word">PlantScanner</span>}
        </S.Brand>
        {collapsed ? (
          <>
            <RailView sections={sections} ui={ui} />
            <S.Foot collapsed={1}>
              <button
                type="button"
                onClick={() => nav.setCollapsed(false)}
                title={t('nav.expand', 'Expandir menu')}
                aria-label={t('nav.expand', 'Expandir menu')}
              >
                <i className="fas fa-angle-double-right" aria-hidden="true" />
              </button>
            </S.Foot>
          </>
        ) : (
          <Panel sections={sections} entries={entries} ui={ui} foot={foot} />
        )}
      </S.Sidebar>
    )

  return (
    <>
      <S.Scrim open={drawerOpen ? 1 : 0} onClick={nav.closeDrawer} />
      <S.Drawer
        open={drawerOpen ? 1 : 0}
        full={mode === 'mobile' ? 1 : 0}
        aria-label={t('nav.main', 'Menu principal')}
        aria-hidden={!drawerOpen}
      >
        <S.DrawerHead staging={isStaging ? 1 : 0}>
          <span className="mark">
            <img src={LogoSvg} alt="" />
          </span>
          <b>PlantScanner</b>
          <S.IconButton
            type="button"
            size={44}
            onClick={nav.closeDrawer}
            aria-label={t('nav.close', 'Fechar menu')}
          >
            <i className="fas fa-times" />
          </S.IconButton>
        </S.DrawerHead>
        {drawerOpen && (
          <Panel sections={sections} entries={entries} ui={ui} foot={foot} showSearch />
        )}
      </S.Drawer>
    </>
  )
})

export default withNamespaces()(withRouter(inject('rootStore')(AppNavigation)))
